import React, { useState, useEffect } from 'react';
import { supabase, Site, Worker, Induction } from '../../lib/supabase';
import { X, ClipboardCheck, Users, Building2 } from 'lucide-react';

interface InductionFormProps {
  induction?: Induction | null;
  onSubmit: (inductionData: Partial<Induction>) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

export const InductionForm: React.FC<InductionFormProps> = ({ induction, onSubmit, onClose, loading = false }) => {
  const [sites, setSites] = useState<Site[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [availableWorkers, setAvailableWorkers] = useState<Worker[]>([]);
  const [existingInductions, setExistingInductions] = useState<Induction[]>([]);
  const [formData, setFormData] = useState({
    site_id: induction?.site_id || '',
    worker_id: induction?.worker_id || '',
    status: induction?.status || 'pending',
  });

  useEffect(() => {
    fetchSitesAndWorkers();
  }, []);

  useEffect(() => {
    if (formData.site_id) {
      fetchExistingInductions(formData.site_id);
    } else {
      setAvailableWorkers(workers);
    }
  }, [formData.site_id, workers]);
  const fetchSitesAndWorkers = async () => {
    try {
      const [sitesData, workersData] = await Promise.all([
        supabase.from('sites').select('*').eq('status', 'active'),
        supabase.from('workers').select('*').eq('status', 'active')
      ]);

      if (sitesData.data) setSites(sitesData.data);
      if (workersData.data) setWorkers(workersData.data);
    } catch (error) {
      console.error('Error fetching sites and workers:', error);
    }
  };

  const fetchExistingInductions = async (siteId: string) => {
    try {
      const { data: inductionsData, error } = await supabase
        .from('inductions')
        .select('*')
        .eq('site_id', siteId)
        .in('status', ['completed', 'pending']);

      if (error) throw error;

      setExistingInductions(inductionsData || []);
      
      // Filter out workers who already have inductions for this site
      const workerIdsWithInductions = new Set(
        (inductionsData || [])
          .filter(ind => !induction || ind.id !== induction.id) // Exclude current induction if editing
          .map(ind => ind.worker_id)
      );
      
      const filtered = workers.filter(worker => !workerIdsWithInductions.has(worker.id));
      setAvailableWorkers(filtered);
      
      // Clear worker selection if the selected worker is no longer available
      if (formData.worker_id && workerIdsWithInductions.has(formData.worker_id) && !induction) {
        setFormData(prev => ({ ...prev, worker_id: '' }));
      }
    } catch (error) {
      console.error('Error fetching existing inductions:', error);
      setAvailableWorkers(workers);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const inductionData = {
      ...formData,
      completed_at: formData.status === 'completed' ? new Date().toISOString() : null
    };

    await onSubmit(inductionData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };


  const selectedSite = sites.find(site => site.id === formData.site_id);
  const selectedWorker = availableWorkers.find(worker => worker.id === formData.worker_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {induction ? 'Edit Induction' : 'New Site Induction'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="inline h-4 w-4 mr-1" />
                Site *
              </label>
              <select
                name="site_id"
                required
                value={formData.site_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select a site</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name} - {site.address}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline h-4 w-4 mr-1" />
                Worker *
              </label>
              <select
                name="worker_id"
                required
                value={formData.worker_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select a worker</option>
                {availableWorkers.map(worker => (
                  <option key={worker.id} value={worker.id}>
                    {worker.name} - {worker.role}
                  </option>
                ))}
              </select>
              {formData.site_id && availableWorkers.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  All workers have already completed or have pending inductions for this site.
                </p>
              )}
              {formData.site_id && existingInductions.length > 0 && availableWorkers.length > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {existingInductions.length} worker(s) already have inductions for this site and are hidden from the list.
                </p>
              )}
            </div>
          </div>

          {selectedSite && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Site Information</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>Type:</strong> {selectedSite.type}</p>
                <p><strong>Address:</strong> {selectedSite.address}</p>
                {selectedSite.requirements && selectedSite.requirements.length > 0 && (
                  <div>
                    <strong>Safety Requirements:</strong>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {selectedSite.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedWorker && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Worker Information</h4>
              <div className="text-sm text-green-800 space-y-1">
                <p><strong>Role:</strong> {selectedWorker.role}</p>
                <p><strong>Email:</strong> {selectedWorker.email}</p>
                {selectedWorker.certifications && selectedWorker.certifications.length > 0 && (
                  <div>
                    <strong>Certifications:</strong>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {selectedWorker.certifications.map((cert, index) => (
                        <li key={index}>{cert}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}


          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (induction ? 'Update Induction' : 'Create Induction')}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};